/**
 * @type hooks
 * @name 公共hooks
 */
import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { getSystem, scrollWithAnimation } from '@arco-design/mobile-utils';
import { GlobalContext } from '../context-provider';
export function useListenResize(resizeHandler, deps, needListen) {
  if (deps === void 0) {
    deps = [];
  }

  if (needListen === void 0) {
    needListen = true;
  }

  useEffect(function () {
    if (!needListen) {
      return function () {};
    }

    window.addEventListener('resize', resizeHandler);
    window.addEventListener('orientationchange', resizeHandler);
    return function () {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('orientationchange', resizeHandler);
    };
  }, [].concat(deps, [needListen]));
}
/**
 * useState自定义封装，统一处理在组件卸载后还使用setState的行为
 * tips：在unmount后有异步处理未完成的场景使用，不推荐无脑替换useState
 * @param initialState 初始状态
 */

export function useMountedState(initialState) {
  var _useState = useState(initialState),
      state = _useState[0],
      setState = _useState[1];

  var leavingRef = useRef(false);
  var setValidState = useCallback(function (value) {
    if (leavingRef.current) {
      return;
    }

    setState(value);
  }, []);
  useEffect(function () {
    return function () {
      leavingRef.current = true;
    };
  }, []);
  var result = [state, setValidState];
  return result;
}
export function useRefState(initialValue) {
  var _useState2 = useState(initialValue),
      state = _useState2[0],
      setState = _useState2[1];

  var stateRef = useRef(state);
  useEffect(function () {
    stateRef.current = state;
  }, [state]);
  return [state, stateRef, setState];
}
export function useRefMountedState(initialValue) {
  var _useMountedState = useMountedState(initialValue),
      state = _useMountedState[0],
      setState = _useMountedState[1];

  var stateRef = useRef(state);
  useEffect(function () {
    stateRef.current = state;
  }, [state]);
  return [state, stateRef, setState];
}
export function useUpdateEffect(effect, dependencies) {
  if (dependencies === void 0) {
    dependencies = [];
  }

  var isInitialMount = useRef(true);
  useEffect(function () {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      return effect();
    }
  }, dependencies);
}
export function useForceUpdate() {
  var _useState3 = useState(0),
      setTick = _useState3[1];

  var update = useCallback(function () {
    setTick(function (tick) {
      return tick + 1;
    });
  }, []);
  return update;
}
export function useSystem() {
  var _useContext = useContext(GlobalContext),
      currentSystem = _useContext.system;

  var _useState4 = useState(function () {
    return currentSystem || getSystem();
  }),
      system = _useState4[0],
      setSystem = _useState4[1];

  useEffect(function () {
    setSystem(currentSystem || getSystem());
  }, [currentSystem]);
  return system;
}
export function useWindowSize(listenResize) {
  var _useState5 = useState(0),
      windowWidth = _useState5[0],
      setWindowWidth = _useState5[1];

  var _useState6 = useState(0),
      windowHeight = _useState6[0],
      setWindowHeight = _useState6[1];

  function setSize() {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  }

  useListenResize(setSize, [], listenResize);
  useEffect(function () {
    setSize();
  }, []);
  return {
    windowWidth: windowWidth,
    windowHeight: windowHeight
  };
}

/* eslint-disable max-lines-per-function */
export function usePopupScroll(visible, popupDom, getScrollContainer, orientationDirection, preventCallback, gestureOutOfControl) {
  if (orientationDirection === void 0) {
    orientationDirection = 'top';
  }

  var scrollRef = useRef([]);
  var touchingRef = useRef({
    dom: null,
    inScrollIndexes: []
  });
  var touchStartYRef = useRef(0);
  var touchStartXRef = useRef(0);
  var handleTouchStart = useCallback(function (e) {
    touchStartXRef.current = e.touches && e.touches[0] ? e.touches[0].clientX : 0;
    touchStartYRef.current = e.touches && e.touches[0] ? e.touches[0].clientY : 0;
    var ele = getScrollContainer ? getScrollContainer() : null;

    if (ele) {
      var actualEle = Array.isArray(ele) ? ele : [ele];
      scrollRef.current = actualEle.reduce(function (acc, nowEle) {
        return [].concat(acc, nowEle && window.getComputedStyle(nowEle).overflow !== 'hidden' ? [{
          ele: nowEle,
          maxScrollX: orientationDirection === 'left' || orientationDirection === 'right' ? nowEle.scrollHeight - nowEle.clientHeight : nowEle.scrollWidth - nowEle.clientWidth,
          maxScrollY: orientationDirection === 'left' || orientationDirection === 'right' ? nowEle.scrollWidth - nowEle.clientWidth : nowEle.scrollHeight - nowEle.clientHeight
        }] : []);
      }, []);
    } else {
      scrollRef.current = [];
    }

    touchingRef.current = {
      dom: null,
      inScrollIndexes: []
    };
  }, [getScrollContainer, orientationDirection]);

  function judgeInScroll(originNode, scroll) {
    var inScrollIndexes = [];

    for (var i = 0; i < scroll.length; i += 1) {
      var node = originNode;

      while (node && scroll[i]) {
        if (node === scroll[i].ele) {
          inScrollIndexes.push(i);
          break;
        } else {
          node = node.parentElement;
        }
      }
    }

    touchingRef.current = {
      dom: originNode,
      inScrollIndexes: inScrollIndexes
    };
    return inScrollIndexes;
  }

  var handleTouchMove = useCallback(function (e) {
    function getScrollTop(scroll) {
      var ele = scroll.ele,
          maxScrollX = scroll.maxScrollX,
          maxScrollY = scroll.maxScrollY;

      switch (orientationDirection) {
        case 'left':
          return maxScrollY - ele.scrollLeft;

        case 'right':
          return ele.scrollLeft;

        case 'bottom':
          return maxScrollX - ele.scrollTop;

        case 'top':
        default:
          return ele.scrollTop;
      }
    }

    function getScrollLeft(scroll) {
      var ele = scroll.ele,
          maxScrollX = scroll.maxScrollX,
          maxScrollY = scroll.maxScrollY;

      switch (orientationDirection) {
        case 'left':
          return ele.scrollTop;

        case 'right':
          return maxScrollX - ele.scrollTop;

        case 'bottom':
          return maxScrollY - ele.scrollLeft;

        case 'top':
        default:
          return ele.scrollLeft;
      }
    }

    var touchMoveX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : 0;
    var touchMoveY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : 0;
    var scroll = scrollRef.current;
    var node = e.target;
    var _touchingRef$current = touchingRef.current,
        touchingDom = _touchingRef$current.dom,
        touchingIndexes = _touchingRef$current.inScrollIndexes; // target一样就取缓存结果，不取dom判断了

    var inScrollIndexes = touchingDom === node ? touchingIndexes : judgeInScroll(node, scroll);
    var distanceX = touchMoveX - touchStartXRef.current;
    var distanceY = touchMoveY - touchStartYRef.current;
    var absX = Math.abs(distanceX);
    var absY = Math.abs(distanceY);
    var direc = absY > absX ? 'y' : 'x';

    if (scroll.length && inScrollIndexes.length) {
      var needPrevents = [];

      for (var i = 0; i < inScrollIndexes.length; i += 1) {
        var touchingScroll = scroll[inScrollIndexes[i]];
        var scrollTop = getScrollTop(touchingScroll);
        var scrollLeft = getScrollLeft(touchingScroll);

        if (distanceY > 0 && absY > absX && scrollTop === 0 || distanceX > 0 && absX > absY && scrollLeft === 0) {
          needPrevents.push(true);
          continue;
        }

        if (distanceY < 0 && absY > absX && scrollTop + 1 >= touchingScroll.maxScrollY || distanceX < 0 && absX > absY && scrollLeft + 1 >= touchingScroll.maxScrollX) {
          needPrevents.push(true);
          continue;
        }

        needPrevents.push(false);
      } // 满足一个不prevent的条件就停止prevent，否则prevent


      if (needPrevents.indexOf(false) < 0) {
        preventCallback && preventCallback(e, direc);
        e.cancelable && e.preventDefault();
      }
    } else {
      preventCallback && preventCallback(e, direc);
      e.cancelable && e.preventDefault();
    }
  }, [orientationDirection, preventCallback]);
  useEffect(function () {
    if (!visible || gestureOutOfControl) {
      return function () {};
    }

    if (popupDom) {
      popupDom.addEventListener('touchstart', handleTouchStart);
      popupDom.addEventListener('touchmove', handleTouchMove, {
        passive: false
      });
    }

    return function () {
      if (popupDom) {
        popupDom.removeEventListener('touchstart', handleTouchStart);
        popupDom.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [visible, popupDom, handleTouchStart, handleTouchMove]);
}
export var useAddListener = function useAddListener(dom, event, handler, options) {
  if (options === void 0) {
    options = {
      capture: true
    };
  }

  useEffect(function () {
    if (!dom) return;

    if (handler) {
      dom.addEventListener(event, handler, options);
    }

    return function () {
      if (handler) {
        dom.removeEventListener(event, handler, options);
      }
    };
  }, [handler]);
}; // 统计同时出现的全屏组件

export var arcoFullScreenCount = 0;
export var arcoFullScreenOriginOverflow = '';
export function usePreventBodyScroll(visible, preventBodyScroll, initialBodyOverflow) {
  // 新出现一个全屏组件则计数+1，并设置body hidden样式
  var addFullScreen = useCallback(function () {
    var count = arcoFullScreenCount; // 在当前没有全屏组件出现时，记下body overflow的初始值

    if (!count) {
      arcoFullScreenOriginOverflow = initialBodyOverflow !== void 0 ? initialBodyOverflow : document.body.style.overflow;
    }

    arcoFullScreenCount += 1;
    document.body.style.overflow = 'hidden';
  }, []); // 移除一个全屏组件时，根据剩余是否还有全屏组件判断是否需要还原overflow样式

  var removeFullScreen = useCallback(function () {
    var newCount = arcoFullScreenCount - 1;
    arcoFullScreenCount = newCount;

    if (!newCount) {
      document.body.style.overflow = arcoFullScreenOriginOverflow;
    }
  }, []);
  useEffect(function () {
    if (!preventBodyScroll) {
      return function () {};
    } // 如果一开始就是visible=true则加上样式


    if (visible) {
      addFullScreen();
    }

    return function () {
      // 如果这里visible为true，则说明即将隐藏组件或在展示时卸载组件
      if (visible) {
        removeFullScreen();
      }
    };
  }, [visible]);
}
export var useProgress = function useProgress(mountedTransition, percentage, duration, mountedBezier, step) {
  var _useState7 = useState(0),
      currentPercentage = _useState7[0],
      setCurrentPercentage = _useState7[1];

  var _useState8 = useState(false),
      transitionControl = _useState8[0],
      setTransitionControl = _useState8[1];

  var _useState9 = useState(0),
      count = _useState9[0],
      setCount = _useState9[1];

  useEffect(function () {
    mountedTransition ? scrollWithAnimation(0, percentage, function (progress) {
      setCurrentPercentage(Math.floor(progress));
    }, duration, mountedBezier) : setCurrentPercentage(percentage);
    var id = setTimeout(function () {
      setTransitionControl(true);
    }, 30);
    return function () {
      return clearTimeout(id);
    };
  }, []);
  useEffect(function () {
    setCount(Math.floor(percentage / step));
  }, [percentage, step]);
  useEffect(function () {
    percentage === 100 ? setCurrentPercentage(100) : setCurrentPercentage(count * step);
  }, [count, percentage, step]);
  return [currentPercentage, transitionControl];
};